import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Card, Typography, Spin, Alert, Select, InputNumber, Button, Space, Tag, Empty, Tooltip, Row, Col, Statistic
} from 'antd';
import {
  ExclamationCircleOutlined, ReloadOutlined, WarningOutlined, CheckCircleOutlined, ShopOutlined, MedicineBoxOutlined
} from '@ant-design/icons';
import lowStockService, { LowStockItem, LowStockResponse } from '../services/lowStockService';
import { useDebounce } from '../hooks/useDebounce';

const { Title, Text } = Typography;
const { Option } = Select;

interface Branch {
  id: number;
  branch_name: string;
}

interface LowStockAlertsProps {
  showThresholdControl?: boolean;
  threshold?: number; // Initial threshold if control is hidden
}

const LowStockAlerts: React.FC<LowStockAlertsProps> = ({ showThresholdControl = true, threshold: initialThreshold = 40 }) => {
  const [lowStockMedicines, setLowStockMedicines] = useState<LowStockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentThreshold, setCurrentThreshold] = useState<number>(initialThreshold);
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [availableBranches, setAvailableBranches] = useState<Branch[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  const debouncedThreshold = useDebounce(currentThreshold, 500);
  const [userRole, setUserRole] = useState<'seller' | 'branch' | 'admin' | null>(null);
  const [currentSellerId, setCurrentSellerId] = useState<number | null>(null);
  const [currentBranchId, setCurrentBranchId] = useState<number | null>(null);

  // Initialize user role and IDs
  useEffect(() => {
    const initializeUser = () => {
      try {
        const userStr = localStorage.getItem('user');
        console.log('LowStockAlerts: Raw user string:', userStr);
        
        if (userStr) {
          const user = JSON.parse(userStr);
          console.log('LowStockAlerts: Parsed user object:', user);
          
          if (user.is_main_seller === true) {
            console.log('LowStockAlerts: User role determined as: seller');
            setUserRole('seller');
            setCurrentSellerId(user.id);
          } else if (user.branch_id) {
            console.log('LowStockAlerts: User role determined as: branch, branch_id:', user.branch_id);
            setUserRole('branch');
            setCurrentBranchId(user.branch_id);
          } else {
            console.log('LowStockAlerts: User role determined as: admin');
            setUserRole('admin');
          }
        } else {
          console.log('LowStockAlerts: No user string found in localStorage');
          setUserRole(null);
        }
      } catch (error) {
        console.error('LowStockAlerts: Error parsing user data:', error);
        setUserRole(null);
      }
    };

    initializeUser();
  }, []);

  const isSeller = userRole === 'seller';
  const sellerId = currentSellerId;
  const branchId = currentBranchId;

  const fetchAvailableBranches = useCallback(async () => {
    if (!isSeller || !sellerId) return;
    
    try {
      const branches = await lowStockService.getSellerBranches(sellerId);
      const branchOptions = branches.map(rel => ({
        id: rel.attributes.branch.id,
        branch_name: rel.attributes.branch.branch_name
      }));
      setAvailableBranches(branchOptions);
      
      // If no branches selected, select all by default
      if (selectedBranches.length === 0 && branchOptions.length > 0) {
        setSelectedBranches(branchOptions.map(b => b.id));
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  }, [isSeller, sellerId, selectedBranches.length]);

  const fetchLowStockAlerts = useCallback(async (page: number, pageSize: number, threshold: number) => {
    // Don't fetch if we don't have the required IDs
    if (isSeller && !sellerId) {
      setError('Seller ID not found. Please log in again.');
      return;
    }
    
    if (!isSeller && !branchId) {
      setError('Branch ID not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      let response: LowStockResponse;
      
      if (isSeller) {
        // For sellers: fetch with optional branch filtering
        const branchIds = selectedBranches.length > 0 ? selectedBranches : undefined;
        response = await lowStockService.getSellerBranchesLowStock(
          sellerId!,
          threshold,
          { page, pageSize, branch_ids: branchIds }
        );
      } else {
        // For branches: fetch only for this branch
        response = await lowStockService.getBranchLowStock(
          branchId!,
          threshold,
          { page, pageSize }
        );
      }
      
      setLowStockMedicines(response.data || []);
      setPagination(prev => ({
        ...prev,
        page: response.meta.pagination.page,
        pageSize: response.meta.pagination.pageSize,
        total: response.meta.pagination.total,
      }));
    } catch (err: any) {
      console.error('Error fetching low stock alerts:', err);
      let errorMessage = 'Failed to fetch low stock alerts.';
      
      if (err.response?.status === 404) {
        errorMessage = 'Low stock alerts API endpoint not found. Please contact your administrator.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error occurred while fetching low stock alerts.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLowStockMedicines([]);
    } finally {
      setLoading(false);
    }
  }, [isSeller, sellerId, branchId, selectedBranches]);

  useEffect(() => {
    if (isSeller && sellerId) {
      fetchAvailableBranches();
    }
  }, [fetchAvailableBranches, isSeller, sellerId]);

  useEffect(() => {
    // Only fetch if we have the required user information
    if (userRole && ((isSeller && sellerId) || (!isSeller && branchId))) {
      fetchLowStockAlerts(pagination.page, pagination.pageSize, debouncedThreshold);
    }
  }, [fetchLowStockAlerts, pagination.page, pagination.pageSize, debouncedThreshold, userRole, isSeller, sellerId, branchId]);

  const handleRefresh = () => {
    fetchLowStockAlerts(pagination.page, pagination.pageSize, currentThreshold);
  };

  const handleBranchChange = (value: number[]) => {
    setSelectedBranches(value);
    // Reset to first page when changing branches
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const columns = [
    {
      title: 'Branch Name',
      dataIndex: ['attributes', 'branch', 'branch_name'],
      key: 'branch_name',
      render: (text: string, record: LowStockItem) => (
        <Space>
          <ShopOutlined />
          <Text strong>{text}</Text>
          {record.attributes.branch_relationship?.relationship_status === 'active' && (
            <Tooltip title="Active Relationship">
              <CheckCircleOutlined style={{ color: 'green' }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Medicine Name',
      dataIndex: ['attributes', 'medicine', 'medicine_name'],
      key: 'medicine_name',
      render: (text: string, record: LowStockItem) => (
        <Space direction="vertical" size={0}>
          <Text strong><MedicineBoxOutlined /> {text}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.attributes.medicine.manufacturer} {record.attributes.medicine.category_name && `(${record.attributes.medicine.category_name})`}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Current Stock',
      dataIndex: ['attributes', 'current_stock'],
      key: 'current_stock',
      render: (stock: number) => {
        let color = 'red';
        if (stock > 0 && stock <= currentThreshold / 2) color = 'orange';
        if (stock > currentThreshold / 2 && stock <= currentThreshold) color = 'gold';
        if (stock > currentThreshold) color = 'green'; // Should not happen in low stock alerts

        return <Tag color={color}>{stock}</Tag>;
      },
      sorter: (a: LowStockItem, b: LowStockItem) => a.attributes.current_stock - b.attributes.current_stock,
    },
    {
      title: 'Threshold',
      key: 'threshold',
      render: () => <Tag color="blue">{currentThreshold}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: LowStockItem) => {
        if (record.attributes.current_stock === 0) {
          return <Tag color="red">Out of Stock</Tag>;
        } else if (record.attributes.current_stock <= currentThreshold) {
          return <Tag color="orange">Low Stock</Tag>;
        }
        return <Tag color="green">Normal</Tag>; // Should not appear in this list
      },
    },
    {
      title: 'Last Updated',
      dataIndex: ['attributes', 'last_updated'],
      key: 'last_updated',
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  const totalAlerts = lowStockMedicines.length;
  const affectedBranches = new Set(lowStockMedicines.map(m => m.attributes.branch.branch_name)).size;

  // Show loading while determining user role
  if (userRole === null) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>Loading user information...</div>
        </div>
      </Card>
    );
  }

  // Show message for admin users
  if (userRole === 'admin') {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Low stock alerts are not available for admin users. Please log in as a seller or branch user to view low stock alerts."
        />
      </Card>
    );
  }

  // Show error if user role cannot be determined
  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <Card
      title={
        <Space>
          <ExclamationCircleOutlined />
          <Title level={4} style={{ margin: 0 }}>
            Low Stock Alerts {isSeller ? '(Seller View)' : '(Branch View)'}
          </Title>
        </Space>
      }
      extra={
        <Space>
          {isSeller && availableBranches.length > 0 && (
            <Space>
              <Text strong>Filter Branches:</Text>
              <Select
                mode="multiple"
                placeholder="Select branches"
                value={selectedBranches}
                onChange={handleBranchChange}
                style={{ minWidth: 200 }}
                maxTagCount="responsive"
              >
                {availableBranches.map(branch => (
                  <Option key={branch.id} value={branch.id}>
                    {branch.branch_name}
                  </Option>
                ))}
              </Select>
            </Space>
          )}
          {showThresholdControl && (
            <Space>
              <Text strong>Threshold:</Text>
              <InputNumber
                min={1}
                value={currentThreshold}
                onChange={(value) => setCurrentThreshold(value === null ? 1 : value)}
                style={{ width: 80 }}
              />
            </Space>
          )}
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Total Alerts"
              value={totalAlerts}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Affected Branches"
              value={affectedBranches}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#08c' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Current Threshold"
              value={currentThreshold}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {lowStockMedicines.length === 0 && !loading ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            isSeller 
              ? "No low stock alerts found for your selected branches."
              : "No low stock alerts found for this branch."
          }
        />
      ) : (
        <Table
          columns={columns}
          dataSource={lowStockMedicines}
          rowKey={(record) => record.id.toString()}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            onChange: (page, pageSize) => setPagination(prev => ({ ...prev, page, pageSize })),
          }}
          scroll={{ x: 'max-content' }}
        />
      )}
    </Card>
  );
};

export default LowStockAlerts;